import {useMemo, useState} from 'react';
import {
  BadgeCheck,
  Eye,
  LogIn,
  MessageSquare,
  Phone,
  Plus,
  Rotate3d,
  Trash2,
  UserPlus,
} from 'lucide-react';
import {Picture} from '../components/Picture';
import {PhotoManager} from '../components/PhotoManager';
import {date, money, number, todayISO} from '../lib/format';
import {useAppLink} from '../shell/AppLink';
import {newId, useStore} from '../store/StoreContext';
import {
  AMENITIES,
  DIRECTION,
  FURNISHING,
  INQUIRY_STATUS,
  LEGAL,
  LISTING_STATUS,
  LISTING_TYPE,
  PROVINCES,
  coverPhoto,
  districtsOf,
  has360,
  type Direction,
  type Furnishing,
  type Legal,
  type Listing,
  type ListingStatus,
  type ListingType,
  type Purpose,
} from '../store/types';
import {
  Button,
  Card,
  ChipToggle,
  EmptyState,
  Field,
  FormGrid,
  Panel,
  Pill,
  Screen,
  Segmented,
  Select,
  Stat,
  TextArea,
  TextInput,
  Toolbar,
} from '../ui/primitives';
import {useIntent, type ManageIntent} from '../shell/AppLink';

function blank(ownerId: string, name: string, phone: string): Listing {
  return {
    address: '',
    amenities: [],
    area: 60,
    bathrooms: 1,
    bedrooms: 2,
    code: `MOC-${Math.floor(1000 + Math.random() * 9000)}`,
    contactName: name,
    contactPhone: phone,
    coverId: null,
    createdAt: todayISO(),
    description: '',
    direction: 'dong-nam',
    district: districtsOf(PROVINCES[0]!)[0]!,
    floors: 1,
    furnishing: 'co-ban',
    id: newId('ls'),
    legal: 'so-hong',
    ownerId,
    photos: [],
    price: 0,
    province: PROVINCES[0]!,
    purpose: 'ban',
    status: 'nhap',
    title: '',
    type: 'can-ho',
    updatedAt: todayISO(),
    verified: false,
    views: 0,
  };
}

/**
 * Quản lý — the seller's side of the same data.
 *
 * It is gated in two steps rather than one: signing in is not the same thing as
 * wanting to sell, so a buyer who lands here is offered the upgrade instead of a
 * dead end, and the upgrade is one click because nothing about it needs review
 * in a demo.
 */
export function ManageApp() {
  const {
    canPost,
    inquiries,
    listings,
    profile,
    removeListing,
    saveListing,
    setInquiryStatus,
    updateProfile,
    user,
  } = useStore();
  const {open} = useAppLink();
  const [tab, setTab] = useState<'tin' | 'lien-he'>('tin');
  const [draft, setDraft] = useState<Listing | null>(null);

  const mine = useMemo(
    () => listings.filter((listing) => listing.ownerId === user?.id),
    [listings, user?.id],
  );

  const myInquiries = useMemo(() => {
    const ids = new Set(mine.map((listing) => listing.id));
    return inquiries.filter((inquiry) => ids.has(inquiry.listingId));
  }, [inquiries, mine]);

  useIntent<ManageIntent>('manage', (intent) => {
    if (intent.tab) setTab(intent.tab);
    if (intent.create && user) {
      setDraft(blank(user.id, profile?.name ?? '', profile?.phone ?? ''));
      return;
    }
    if (intent.listingId) {
      const listing = listings.find((entry) => entry.id === intent.listingId);
      if (listing) setDraft({...listing});
    }
  });

  if (!user) {
    return (
      <Screen>
        <Card title="Đăng nhập để quản lý tin">
          <p className="text-[13px] text-ink-2">
            Tin đăng, ảnh 360 và liên hệ từ người mua đều gắn với tài khoản của bạn. Đăng nhập hoặc
            tạo tài khoản mới — mất khoảng 30 giây.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button tone="primary" icon={<LogIn size={15} />} onClick={() => open('account')}>
              Đăng nhập
            </Button>
            <Button
              icon={<UserPlus size={15} />}
              onClick={() => open('account', {register: true})}
            >
              Tạo tài khoản
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  if (!canPost) {
    return (
      <Screen>
        <Card title="Bật quyền đăng tin">
          <p className="text-[13px] text-ink-2">
            Tài khoản của bạn đang ở chế độ người mua. Bật quyền đăng tin để đăng bất động sản, tải
            ảnh 360 và nhận liên hệ trực tiếp từ người mua.
          </p>
          <div className="mt-4">
            <Button
              tone="primary"
              icon={<BadgeCheck size={15} />}
              onClick={() => void updateProfile({role: 'nguoi-ban'})}
            >
              Bật quyền đăng tin
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  const published = mine.filter((listing) => listing.status === 'dang-ban');
  const totalViews = mine.reduce((sum, listing) => sum + listing.views, 0);
  const fresh = myInquiries.filter((inquiry) => inquiry.status === 'moi').length;
  const editing = draft ? listings.some((listing) => listing.id === draft.id) : false;
  const canSave = Boolean(draft && draft.title.trim() && draft.price > 0 && draft.area > 0);

  return (
    <Screen
      toolbar={
        <Toolbar>
          <Segmented
            value={tab}
            onChange={setTab}
            options={[
              {label: `Tin của tôi (${mine.length})`, value: 'tin'},
              {label: `Liên hệ${fresh > 0 ? ` · ${fresh} mới` : ''}`, value: 'lien-he'},
            ]}
          />
          <span className="flex-1" />
          <Button
            tone="primary"
            icon={<Plus size={16} />}
            onClick={() => setDraft(blank(user.id, profile?.name ?? '', profile?.phone ?? ''))}
          >
            Đăng tin mới
          </Button>
        </Toolbar>
      }
    >
      <div className="mb-4 grid grid-cols-2 gap-3 @3xl:grid-cols-4">
        <Stat label="Tin đã đăng" value={number(mine.length)} hint={`${published.length} đang hiển thị`} />
        <Stat label="Lượt xem" value={number(totalViews)} />
        <Stat label="Liên hệ" value={number(myInquiries.length)} hint={`${fresh} chưa xử lý`} />
        <Stat
          label="Tin có 360°"
          value={number(mine.filter(has360).length)}
          hint="Tin có 360 được xem lâu hơn"
        />
      </div>

      {tab === 'tin' ? (
        mine.length === 0 ? (
          <EmptyState
            title="Bạn chưa có tin đăng nào"
            hint="Bấm “Đăng tin mới”, thêm vài tấm ảnh và một ảnh 360 — tin sẽ xuất hiện ngay ở app Thị trường."
          />
        ) : (
          <ul className="space-y-3">
            {mine.map((listing) => {
              const messages = myInquiries.filter((inquiry) => inquiry.listingId === listing.id);
              return (
                <li key={listing.id} className="surface-sm rounded-card p-3">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setDraft({...listing})}
                      className="w-28 shrink-0"
                    >
                      <Picture
                        alt={listing.title}
                        className="rounded-control"
                        ratio="4 / 3"
                        refItem={coverPhoto(listing)?.ref ?? null}
                      />
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={LISTING_STATUS[listing.status].tone}>
                          {LISTING_STATUS[listing.status].label}
                        </Pill>
                        {has360(listing) ? (
                          <Pill tone="violet">
                            <Rotate3d size={11} className="mr-1" /> 360°
                          </Pill>
                        ) : null}
                        <span className="flex items-center gap-1 text-[11.5px] text-ink-3">
                          <Eye size={12} /> {number(listing.views)}
                        </span>
                        {messages.length > 0 ? (
                          <span className="flex items-center gap-1 text-[11.5px] text-ink-3">
                            <MessageSquare size={12} /> {messages.length}
                          </span>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={() => setDraft({...listing})}
                        className="mt-1.5 block w-full text-left"
                      >
                        <p className="truncate text-[14px] font-bold text-ink">{listing.title || '(chưa có tiêu đề)'}</p>
                        <p className="truncate text-[12px] text-ink-3">
                          {listing.code} · {money(listing.price)}
                          {listing.purpose === 'cho-thue' ? '/tháng' : ''} · {listing.district}
                        </p>
                      </button>

                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button onClick={() => setDraft({...listing})}>Sửa</Button>
                        {listing.status === 'dang-ban' ? (
                          <Button onClick={() => void saveListing({...listing, status: 'tam-an'})}>
                            Tạm ẩn
                          </Button>
                        ) : (
                          <Button
                            tone="primary"
                            onClick={() => void saveListing({...listing, status: 'dang-ban'})}
                          >
                            Đăng bán
                          </Button>
                        )}
                        {listing.status !== 'da-ban' ? (
                          <Button onClick={() => void saveListing({...listing, status: 'da-ban'})}>
                            Đánh dấu đã bán
                          </Button>
                        ) : null}
                        <Button onClick={() => open('market', {listingId: listing.id})}>
                          Xem như người mua
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )
      ) : myInquiries.length === 0 ? (
        <EmptyState
          title="Chưa có liên hệ nào"
          hint="Khi người mua bấm “Gửi liên hệ” trên tin của bạn, tin nhắn và số điện thoại của họ sẽ nằm ở đây."
        />
      ) : (
        <ul className="space-y-3">
          {myInquiries.map((inquiry) => {
            const listing = listings.find((entry) => entry.id === inquiry.listingId);
            return (
              <li key={inquiry.id} className="surface-sm rounded-card p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone={INQUIRY_STATUS[inquiry.status].tone}>
                    {INQUIRY_STATUS[inquiry.status].label}
                  </Pill>
                  <span className="text-[12px] font-semibold text-ink">{inquiry.name}</span>
                  <a
                    href={`tel:${inquiry.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-1 text-[12px] font-semibold text-accent"
                  >
                    <Phone size={12} /> {inquiry.phone}
                  </a>
                  <span className="ml-auto text-[11.5px] text-ink-3">{date(inquiry.at)}</span>
                </div>

                <p className="mt-2 text-[13px] text-ink-2">{inquiry.message || '(không có lời nhắn)'}</p>

                <p className="mt-2 text-[11.5px] text-ink-3">
                  Tin: {listing?.title ?? 'đã xoá'}
                  {inquiry.visitAt ? ` · muốn xem nhà ${date(inquiry.visitAt)}` : ''}
                </p>

                <div className="mt-3 flex flex-wrap gap-2">
                  {inquiry.status !== 'da-lien-he' ? (
                    <Button onClick={() => void setInquiryStatus(inquiry.id, 'da-lien-he')}>
                      Đánh dấu đã liên hệ
                    </Button>
                  ) : null}
                  {inquiry.status !== 'dong' ? (
                    <Button onClick={() => void setInquiryStatus(inquiry.id, 'dong')}>Đóng</Button>
                  ) : (
                    <Button onClick={() => void setInquiryStatus(inquiry.id, 'moi')}>Mở lại</Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {draft ? (
        <Panel
          wide
          title={editing ? `Sửa tin ${draft.code}` : 'Đăng tin mới'}
          onClose={() => setDraft(null)}
          footer={
            <div className="flex items-center justify-between gap-2">
              {editing ? (
                <Button
                  tone="danger"
                  icon={<Trash2 size={15} />}
                  onClick={() => {
                    void removeListing(draft.id);
                    setDraft(null);
                  }}
                >
                  Xoá tin
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button
                  disabled={!canSave}
                  onClick={() => {
                    void saveListing({...draft, status: 'nhap'});
                    setDraft(null);
                  }}
                >
                  Lưu nháp
                </Button>
                <Button
                  tone="primary"
                  disabled={!canSave}
                  onClick={() => {
                    void saveListing({
                      ...draft,
                      status: draft.status === 'nhap' ? 'dang-ban' : draft.status,
                    });
                    setDraft(null);
                  }}
                >
                  {draft.status === 'nhap' ? 'Đăng bán' : 'Lưu thay đổi'}
                </Button>
              </div>
            </div>
          }
        >
          <div className="space-y-4">
            <Card title="Thông tin cơ bản">
              <div className="mb-4">
                <Segmented
                  value={draft.purpose}
                  onChange={(purpose: Purpose) => setDraft({...draft, purpose})}
                  options={[
                    {label: 'Bán', value: 'ban'},
                    {label: 'Cho thuê', value: 'cho-thue'},
                  ]}
                />
              </div>
              <FormGrid>
                <Field label="Tiêu đề tin" wide hint="Ngắn, có loại nhà và khu vực">
                  <TextInput
                    value={draft.title}
                    placeholder="Căn hộ 2PN view công viên, Phú Mỹ Hưng"
                    onChange={(title) => setDraft({...draft, title})}
                  />
                </Field>
                <Field label="Loại bất động sản">
                  <Select
                    value={draft.type}
                    onChange={(value) => setDraft({...draft, type: value as ListingType})}
                    options={Object.entries(LISTING_TYPE).map(([value, label]) => ({label, value}))}
                  />
                </Field>
                <Field
                  label={draft.purpose === 'cho-thue' ? 'Giá thuê mỗi tháng (VND)' : 'Giá bán (VND)'}
                  hint={money(draft.price)}
                >
                  <TextInput
                    type="number"
                    value={draft.price}
                    onChange={(price) => setDraft({...draft, price: Number(price) || 0})}
                  />
                </Field>
                <Field label="Tỉnh / thành">
                  <Select
                    value={draft.province}
                    onChange={(province) =>
                      setDraft({...draft, district: districtsOf(province)[0] ?? '', province})
                    }
                    options={PROVINCES.map((value) => ({label: value, value}))}
                  />
                </Field>
                <Field label="Quận / huyện">
                  <Select
                    value={draft.district}
                    onChange={(district) => setDraft({...draft, district})}
                    options={districtsOf(draft.province).map((value) => ({label: value, value}))}
                  />
                </Field>
                <Field label="Địa chỉ" wide>
                  <TextInput
                    value={draft.address}
                    onChange={(address) => setDraft({...draft, address})}
                  />
                </Field>
              </FormGrid>
            </Card>

            <Card title="Chi tiết">
              <FormGrid>
                <Field label="Diện tích (m²)">
                  <TextInput
                    type="number"
                    value={draft.area}
                    onChange={(area) => setDraft({...draft, area: Number(area) || 0})}
                  />
                </Field>
                <Field label="Số tầng">
                  <TextInput
                    type="number"
                    value={draft.floors}
                    onChange={(floors) => setDraft({...draft, floors: Number(floors) || 1})}
                  />
                </Field>
                <Field label="Phòng ngủ">
                  <TextInput
                    type="number"
                    value={draft.bedrooms}
                    onChange={(bedrooms) => setDraft({...draft, bedrooms: Number(bedrooms) || 0})}
                  />
                </Field>
                <Field label="Phòng tắm">
                  <TextInput
                    type="number"
                    value={draft.bathrooms}
                    onChange={(bathrooms) => setDraft({...draft, bathrooms: Number(bathrooms) || 0})}
                  />
                </Field>
                <Field label="Hướng">
                  <Select
                    value={draft.direction}
                    onChange={(value) => setDraft({...draft, direction: value as Direction})}
                    options={Object.entries(DIRECTION).map(([value, label]) => ({label, value}))}
                  />
                </Field>
                <Field label="Pháp lý">
                  <Select
                    value={draft.legal}
                    onChange={(value) => setDraft({...draft, legal: value as Legal})}
                    options={Object.entries(LEGAL).map(([value, label]) => ({label, value}))}
                  />
                </Field>
                <Field label="Nội thất" wide>
                  <Select
                    value={draft.furnishing}
                    onChange={(value) => setDraft({...draft, furnishing: value as Furnishing})}
                    options={Object.entries(FURNISHING).map(([value, label]) => ({label, value}))}
                  />
                </Field>
              </FormGrid>

              <div className="mt-4">
                <p className="mb-2 text-[12px] font-semibold text-ink-2">Tiện ích</p>
                <div className="flex flex-wrap gap-2">
                  {AMENITIES.map((item) => (
                    <ChipToggle
                      key={item}
                      active={draft.amenities.includes(item)}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          amenities: draft.amenities.includes(item)
                            ? draft.amenities.filter((entry) => entry !== item)
                            : [...draft.amenities, item],
                        })
                      }
                    >
                      {item}
                    </ChipToggle>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <Field label="Mô tả">
                  <TextArea
                    rows={5}
                    value={draft.description}
                    placeholder="Vị trí, hiện trạng, nội thất bàn giao, lý do bán…"
                    onChange={(description) => setDraft({...draft, description})}
                  />
                </Field>
              </div>
            </Card>

            <Card title="Hình ảnh & ảnh 360°">
              <PhotoManager
                photos={draft.photos}
                coverId={draft.coverId}
                onChange={(photos, coverId) => setDraft({...draft, coverId, photos})}
              />
            </Card>

            <Card title="Liên hệ & trạng thái">
              <FormGrid>
                <Field label="Tên hiển thị">
                  <TextInput
                    value={draft.contactName}
                    onChange={(contactName) => setDraft({...draft, contactName})}
                  />
                </Field>
                <Field label="Số điện thoại">
                  <TextInput
                    type="tel"
                    value={draft.contactPhone}
                    onChange={(contactPhone) => setDraft({...draft, contactPhone})}
                  />
                </Field>
                <Field label="Trạng thái tin" wide>
                  <Select
                    value={draft.status}
                    onChange={(value) => setDraft({...draft, status: value as ListingStatus})}
                    options={Object.entries(LISTING_STATUS).map(([value, entry]) => ({
                      label: entry.label,
                      value,
                    }))}
                  />
                </Field>
              </FormGrid>
            </Card>
          </div>
        </Panel>
      ) : null}
    </Screen>
  );
}
